import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Keyboard } from "@/components/Keyboard";
import { playSoft, kickAudio } from "@/audio/piano";
import { useMidiInput, type MidiInputNote } from "@/audio/midiInput";
import {
  type GradeMode,
  type PlayedNote,
  parsePitch,
  initSequence,
  feedSequence,
  initSet,
  feedSet,
  initChord,
  evaluateChord,
  accuracy as calcAccuracy,
  stars as calcStars,
  type SequenceState,
  type SetState,
} from "@/grading/grade";

/**
 * PracticeChallenge — the graded "try it yourself" widget.
 *
 * Reuses the lesson's OWN target pitches (the same ones it already highlights
 * for teaching), so we never invent notes — we grade against vetted data.
 *
 * Input works two ways, no setup required:
 *   • A real MIDI keyboard (Web MIDI) — click "Connect MIDI keyboard".
 *   • The on-screen keyboard — always available, so students with no hardware
 *     can still complete the challenge.
 *
 * Wait-mode grading (sequence): the next expected note glows; a correct note
 * advances, a wrong note flashes red and counts against accuracy but never
 * blocks. Chord mode grades the held set; set mode grades an order-free pool.
 */

const GREEN = "#22c55e";
const AMBER = "#f59e0b";
const RED = "#ef4444";
const DIM = "#52525b";

export interface PracticeChallengeProps {
  /** Target pitches in SPN ("C4","E4","G4"). For sequence, order matters. */
  notes: string[];
  mode: GradeMode;
  /** Short instruction, e.g. "Play the C major scale, thumb on C." */
  prompt: string;
  /** Optional title above the prompt. */
  title?: string;
  /** Exact-octave matching (default false = pitch-class, octave-forgiving). */
  octaveSensitive?: boolean;
  /** Keyboard render hints. Auto-inferred from the target if omitted. */
  startOctave?: number;
  octaves?: number;
  height?: number;
  /** Persistence + LMS reporting keys. */
  courseSlug?: string;
  order?: string | number;
  id?: string;
}

function pitchLabel(p: string): string {
  return p.replace(/-?\d+$/, "").replace("#", "♯").replace("b", "♭");
}

// Normalise to sharp spelling so a flat-spelled target ("Db4") and the MIDI
// input's sharp spelling ("C#4") key the SAME entry in the highlight map —
// otherwise a red/green flash can be masked by the amber dot on that key.
const FLAT_TO_SHARP: Record<string, string> = { Db: "C#", Eb: "D#", Gb: "F#", Ab: "G#", Bb: "A#" };
function sharpName(p: string): string {
  const m = p.match(/^([A-G])(b|#)?(-?\d+)$/);
  if (!m) return p;
  const [, letter, acc, oct] = m;
  if (acc === "b") {
    const repl = FLAT_TO_SHARP[letter + "b"];
    if (repl) return repl + oct;
  }
  return p;
}

function inferRange(notes: string[]): { startOctave: number; octaves: number } {
  const midis = notes.map((n) => parsePitch(n)?.midi).filter((m): m is number => m != null);
  if (midis.length === 0) return { startOctave: 4, octaves: 2 };
  const lowOct = Math.floor(Math.min(...midis) / 12) - 1;
  const highOct = Math.floor(Math.max(...midis) / 12) - 1;
  return { startOctave: Math.max(0, lowOct), octaves: Math.max(1, highOct - lowOct + 1) };
}

type Phase = "idle" | "playing" | "done";

export function PracticeChallenge({
  notes,
  mode,
  prompt,
  title,
  octaveSensitive = false,
  startOctave: startOctaveProp,
  octaves: octavesProp,
  height = 170,
  courseSlug,
  order,
  id = "main",
}: PracticeChallengeProps) {
  const range = useMemo(() => inferRange(notes), [notes]);
  const startOctave = startOctaveProp ?? range.startOctave;
  const octaves = octavesProp ?? range.octaves;

  const [phase, setPhase] = useState<Phase>("idle");
  const [seq, setSeq] = useState<SequenceState>(() => initSequence(notes));
  const [set, setSet] = useState<SetState>(() => initSet(notes));
  const [held, setHeld] = useState<number[]>([]); // midis currently held (chord mode)
  const [chordMatched, setChordMatched] = useState<number[]>([]); // target indices matched
  const [chordDone, setChordDone] = useState(false);
  const [chordErrors, setChordErrors] = useState(0); // wrong presses in chord mode
  const [flash, setFlash] = useState<{ pitch: string; verdict: "correct" | "wrong" } | null>(null);
  const [best, setBest] = useState<number | null>(null);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // Authoritative grading state lives in refs and is MIRRORED to React state.
  // Grading + side effects (flash, finish) must NOT run inside set-state
  // updaters: React StrictMode double-invokes updaters, which double-reported
  // every completed challenge to the LMS.
  const seqRef = useRef<SequenceState>(seq);
  const setModeRef = useRef<SetState>(set);
  const chordErrorsRef = useRef(0);
  const finishedRef = useRef(false);
  const flashTimer = useRef<number | null>(null);
  const clickRelease = useRef<Map<number, number>>(new Map());

  const chordTarget = useMemo(() => initChord(notes), [notes]);
  const storageKey =
    courseSlug && order != null ? `efl-grade:${courseSlug}/${order}/${id}` : null;

  // Load best score
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const v = parseInt(raw, 10);
        // Guard corrupt values: NaN here would also block all future bests
        // (NaN comparisons are always false).
        if (Number.isFinite(v)) setBest(Math.min(100, Math.max(0, v)));
      }
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const reset = useCallback(() => {
    seqRef.current = initSequence(notes);
    setModeRef.current = initSet(notes);
    chordErrorsRef.current = 0;
    finishedRef.current = false;
    setSeq(seqRef.current);
    setSet(setModeRef.current);
    setChordErrors(0);
    setHeld([]);
    setChordMatched([]);
    setChordDone(false);
    setFlash(null);
  }, [notes]);

  // Re-init whenever the target changes (lesson swaps the chord/scale).
  useEffect(() => {
    reset();
    phaseRef.current = "idle"; // sync immediately — the render-mirror lags one frame
    setPhase("idle");
  }, [notes, mode, reset]);

  const doFlash = useCallback((pitch: string, verdict: "correct" | "wrong") => {
    setFlash({ pitch, verdict });
    if (flashTimer.current != null) clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 260);
  }, []);

  const finish = useCallback(
    (hits: number, errors: number) => {
      // Idempotent: a key smashed together with the finishing note (or a
      // StrictMode re-render) must never report the grade twice.
      if (finishedRef.current) return;
      finishedRef.current = true;
      phaseRef.current = "done";
      const acc = calcAccuracy(hits, errors);
      setPhase("done");
      if (storageKey && (best == null || acc > best)) {
        setBest(acc);
        try {
          localStorage.setItem(storageKey, String(acc));
        } catch {
          /* ignore */
        }
      }
      // Report up to the LMS so progress can be persisted server-side later.
      try {
        const LMS_ORIGIN = "https://learn-music.nathanielschool.com";
        if (window.parent !== window) window.parent.postMessage(
          { type: "companion-grade", course: courseSlug, order, id, accuracy: acc, stars: calcStars(acc) },
          LMS_ORIGIN
        );
      } catch {
        /* not embedded — ignore */
      }
    },
    [best, storageKey, courseSlug, order, id]
  );

  // ── Core input handler: one played note enters the grader ──────────────────
  // Grading runs on the REFS (synchronous source of truth) and the result is
  // mirrored into React state. Keeping feedSequence/doFlash/finish OUT of
  // set-state updaters matters: updaters must be pure (StrictMode
  // double-invokes them), and the ref read means two rapid notes can't both
  // grade against the same stale state.
  const onPlayed = useCallback(
    (played: PlayedNote, pitchName: string) => {
      if (finishedRef.current || phaseRef.current === "done") return;
      if (phaseRef.current === "idle") {
        phaseRef.current = "playing";
        setPhase("playing");
      }

      if (mode === "sequence") {
        const { state, verdict } = feedSequence(seqRef.current, played, { octaveSensitive });
        seqRef.current = state;
        setSeq(state);
        doFlash(pitchName, verdict);
        if (state.done) finish(state.hits, state.errors);
      } else if (mode === "set") {
        const { state, verdict } = feedSet(setModeRef.current, played, { octaveSensitive });
        setModeRef.current = state;
        setSet(state);
        if (verdict !== "repeat") doFlash(pitchName, verdict === "correct" ? "correct" : "wrong");
        if (state.done) finish(state.hits, state.errors);
      }
      // chord mode is handled by held-set effect below
    },
    [mode, octaveSensitive, doFlash, finish]
  );

  // ── Chord mode: evaluate the held set whenever it changes ──────────────────
  useEffect(() => {
    if (mode !== "chord" || phase === "done") return;
    if (held.length === 0) return;
    const result = evaluateChord(chordTarget, held, { octaveSensitive });
    setChordMatched(result.matchedIndices);
    if (result.correct && !chordDone) {
      setChordDone(true);
      // Score the chord like the other modes: target size = hits, and every
      // out-of-chord press along the way counts against accuracy.
      finish(chordTarget.parsed.length, chordErrorsRef.current);
    }
  }, [held, mode, phase, chordTarget, octaveSensitive, chordDone, finish]);

  // One wrong press in chord mode = one error (shared by MIDI + on-screen).
  const recordChordPress = useCallback(
    (midiNum: number, pc: number, pitchName: string) => {
      if (finishedRef.current) return;
      if (phaseRef.current === "idle") {
        phaseRef.current = "playing";
        setPhase("playing");
      }
      const inChord = chordTarget.parsed.some((t) =>
        octaveSensitive ? t.midi === midiNum : t.pc === pc
      );
      if (!inChord) {
        chordErrorsRef.current += 1;
        setChordErrors(chordErrorsRef.current);
      }
      doFlash(pitchName, inChord ? "correct" : "wrong");
    },
    [chordTarget, octaveSensitive, doFlash]
  );

  // ── MIDI input ─────────────────────────────────────────────────────────────
  const midi = useMidiInput({
    onNoteOn: (n: MidiInputNote) => {
      kickAudio();
      // playSoft, NOT playNow: chord tones must ring together, and a keypress
      // must never silence a backing track mid-exercise.
      void playSoft(n.pitchName, "8n", Math.max(0.4, n.velocity));
      if (mode === "chord") {
        setHeld((prev) => (prev.includes(n.midi) ? prev : [...prev, n.midi]));
        recordChordPress(n.midi, n.pc, n.pitchName);
      } else {
        onPlayed({ midi: n.midi, pc: n.pc }, n.pitchName);
      }
    },
    onNoteOff: (n: MidiInputNote) => {
      if (mode === "chord") setHeld((prev) => prev.filter((m) => m !== n.midi));
    },
  });

  // ── On-screen keyboard input ───────────────────────────────────────────────
  const onScreenPlay = useCallback(
    (pitch: string) => {
      const parsed = parsePitch(pitch);
      if (!parsed) return;
      const played: PlayedNote = { midi: parsed.midi, pc: parsed.pc };
      if (mode === "chord") {
        // No sustain on click — accumulate briefly, then auto-release.
        setHeld((prev) => (prev.includes(parsed.midi) ? prev : [...prev, parsed.midi]));
        recordChordPress(parsed.midi, parsed.pc, pitch);
        const existing = clickRelease.current.get(parsed.midi);
        if (existing) clearTimeout(existing);
        const t = window.setTimeout(() => {
          setHeld((prev) => prev.filter((m) => m !== parsed.midi));
          clickRelease.current.delete(parsed.midi);
        }, 900);
        clickRelease.current.set(parsed.midi, t);
      } else {
        onPlayed(played, pitch);
      }
    },
    [mode, recordChordPress, onPlayed]
  );

  useEffect(() => {
    return () => {
      if (flashTimer.current != null) clearTimeout(flashTimer.current);
      clickRelease.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  // ── Keyboard highlights from grading state ─────────────────────────────────
  // Build with an explicit priority and de-dupe by key, because the on-screen
  // Keyboard matches a highlight to a key by first-match. Priority (high→low):
  // flash (transient) > current next-note (amber) > already-correct (green).
  // Without this, a descending scale (C-D-E-F-G-F-E-D-C) would mask the amber
  // "next note" glow behind a green "already played" dot of the same key.
  const highlights = useMemo(() => {
    const FLASH_P = 3, CURRENT_P = 2, DONE_P = 1;
    const byKey = new Map<string, { pitch: string; color: string; label?: string; prio: number }>();
    const put = (rawPitch: string, color: string, label: string, prio: number) => {
      // Key by sharp spelling so "Db4" (target) and "C#4" (MIDI flash) merge.
      const pitch = sharpName(rawPitch);
      const existing = byKey.get(pitch);
      if (!existing || prio > existing.prio) byKey.set(pitch, { pitch, color, label, prio });
    };

    if (mode === "sequence") {
      seq.steps
        .filter((s) => s.status === "correct")
        .forEach((s) => put(s.pitch, GREEN, "✓", DONE_P));
      const current = seq.steps[seq.cursor];
      if (current && phase !== "done") put(current.pitch, AMBER, "▶", CURRENT_P);
    } else if (mode === "set") {
      set.steps.forEach((s) =>
        put(s.pitch, s.status === "correct" ? GREEN : AMBER, s.status === "correct" ? "✓" : "", s.status === "correct" ? DONE_P : CURRENT_P)
      );
    } else {
      chordTarget.pitches.forEach((p, i) => {
        const matched = chordMatched.includes(i);
        put(p, matched ? GREEN : AMBER, matched ? "✓" : "", matched ? DONE_P : CURRENT_P);
      });
    }
    if (flash) {
      put(flash.pitch, flash.verdict === "correct" ? GREEN : RED, flash.verdict === "wrong" ? "✗" : "", FLASH_P);
    }
    return Array.from(byKey.values()).map(({ pitch, color, label }) => ({ pitch, color, label }));
  }, [mode, seq, set, chordTarget, chordMatched, flash, phase]);

  // ── Progress + score ───────────────────────────────────────────────────────
  const progress = useMemo(() => {
    if (mode === "sequence") return seq.steps.length ? seq.cursor / seq.steps.length : 0;
    if (mode === "set") return set.steps.length ? set.steps.filter((s) => s.status === "correct").length / set.steps.length : 0;
    return chordTarget.parsed.length ? chordMatched.length / chordTarget.parsed.length : 0;
  }, [mode, seq, set, chordMatched, chordTarget]);

  const hits = mode === "sequence" ? seq.hits : mode === "set" ? set.hits : chordMatched.length;
  const errors = mode === "sequence" ? seq.errors : mode === "set" ? set.errors : chordErrors;
  const liveAcc = calcAccuracy(hits, errors);
  const finalStars = calcStars(liveAcc);

  const restart = () => {
    kickAudio();
    reset();
    phaseRef.current = "playing"; // sync immediately — a fast MIDI note right
    setPhase("playing");          // after "Try again" must not be swallowed
  };

  return (
    <div className="my-4 rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-950/20 via-zinc-900/40 to-emerald-950/10 p-4 shadow-[0_8px_30px_rgba(0,0,0,0.45)]">
      {/* Header */}
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-300">
            ♪ Practice &amp; grade{title ? ` · ${title}` : ""}
          </div>
          <p className="mt-1 text-[13px] leading-snug text-nsm-dim">{prompt}</p>
        </div>
        <div className="flex items-center gap-2">
          {best != null && (
            <span className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-300">
              Best {best}%
            </span>
          )}
          {midi.status === "ready" ? (
            <span className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-300">
              ⌁ {midi.devices[0] ?? "MIDI connected"}
            </span>
          ) : midi.status === "unsupported" ? (
            <span className="rounded-md border border-zinc-600/40 bg-zinc-700/20 px-2 py-1 text-[11px] text-nsm-dim">
              On-screen keyboard
            </span>
          ) : (
            <button
              onClick={() => void midi.enable()}
              disabled={midi.status === "requesting"}
              className="rounded-md border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-semibold text-amber-200 transition hover:bg-amber-500/20 disabled:cursor-wait disabled:opacity-60"
            >
              {midi.status === "requesting" ? "Connecting…" : "⌁ Connect MIDI keyboard"}
            </button>
          )}
        </div>
      </div>

      {/* Target strip */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {mode === "sequence"
          ? seq.steps.map((s, i) => (
              <span
                key={i}
                className="inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-[12px] font-bold transition"
                style={{
                  background:
                    s.status === "correct" ? `${GREEN}22` : s.status === "current" ? `${AMBER}22` : "#27272a",
                  color: s.status === "correct" ? GREEN : s.status === "current" ? AMBER : DIM,
                  border: `1px solid ${s.status === "correct" ? GREEN : s.status === "current" ? AMBER : "#3f3f46"}55`,
                }}
              >
                {pitchLabel(s.pitch)}
              </span>
            ))
          : (mode === "set" ? set.steps.map((s) => ({ pitch: s.pitch, ok: s.status === "correct" }))
              : chordTarget.pitches.map((p, i) => ({ pitch: p, ok: chordMatched.includes(i) }))
            ).map((s, i) => (
              <span
                key={i}
                className="inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-[12px] font-bold transition"
                style={{
                  background: s.ok ? `${GREEN}22` : `${AMBER}18`,
                  color: s.ok ? GREEN : AMBER,
                  border: `1px solid ${s.ok ? GREEN : AMBER}55`,
                }}
              >
                {pitchLabel(s.pitch)}
              </span>
            ))}
        {mode === "chord" && <span className="ml-1 text-[11px] text-nsm-dim">play together</span>}
      </div>

      {/* Keyboard */}
      <Keyboard
        startOctave={startOctave}
        octaves={octaves}
        highlights={highlights}
        onPlay={onScreenPlay}
        height={height}
      />

      {/* Footer: progress + score + controls */}
      <div className="mt-3">
        <div className="h-1.5 overflow-hidden rounded-full bg-zinc-800">
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-emerald-400 transition-[width] duration-150"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3 text-[12px] text-nsm-dim">
            <span>
              <span className="font-semibold text-emerald-300">{hits}</span> correct
            </span>
            <span>
              <span className="font-semibold text-rose-300">{errors}</span> missed
            </span>
            {phase !== "idle" && (
              <span>
                Accuracy <span className="font-semibold text-amber-200">{liveAcc}%</span>
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {phase === "done" && (
              <span className="text-[14px] tracking-widest" aria-label={`${finalStars} of 3 stars`}>
                {"★".repeat(finalStars)}
                <span className="text-zinc-700">{"★".repeat(3 - finalStars)}</span>
              </span>
            )}
            <button
              onClick={restart}
              className="rounded-lg bg-amber-500 px-3 py-1.5 text-[12px] font-bold text-zinc-900 transition hover:bg-amber-400"
            >
              {phase === "idle" ? "Start" : phase === "done" ? "Try again" : "Restart"}
            </button>
          </div>
        </div>

        {phase === "done" && (
          <div
            className="mt-2 rounded-lg border px-3 py-2 text-[13px]"
            style={{
              borderColor: finalStars >= 2 ? `${GREEN}55` : `${AMBER}55`,
              background: finalStars >= 2 ? `${GREEN}12` : `${AMBER}10`,
              color: finalStars >= 2 ? GREEN : AMBER,
            }}
          >
            {finalStars === 3 && "Perfect — clean run. Try it a touch faster, or in a new key."}
            {finalStars === 2 && "Nicely done. Slow down and aim for a flawless pass next."}
            {finalStars === 1 && "Got there. Reset and play it slowly — accuracy before speed."}
            {finalStars === 0 && "Keep going. Watch the glowing key and take it one note at a time."}
          </div>
        )}

        <p className="mt-2 text-[11px] text-nsm-dim">
          {midi.status === "ready"
            ? "Play on your MIDI keyboard, or tap the keys above. The glowing key is your next note."
            : "Tap the keys above to play — or connect a MIDI keyboard for hands-on practice. The glowing key is your next note."}
        </p>
        {midi.error && <p className="mt-1 text-[11px] text-rose-300">⚠ {midi.error}</p>}
      </div>
    </div>
  );
}
